class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.itemsCart = this.querySelector('cart-drawer-items');
    if (this.itemsCart){
      this.itemsCart.addEventListener('wheel', this.onScroll.bind(this));
    }

    this.drawerInner = this.querySelector('.drawer__inner');
    if (this.drawerInner){
      this.drawerInner.addEventListener('wheel', this.onScrollInner.bind(this));
    }

    // if URL contains /?fromcart=true, open the cart drawer
    if (window.location.search.includes('fromcart=true')) {
      this.open();
    }
  }

  onScrollInner(event) {
    if (!this.isOpen) return;
    const isScrollingUp = event.deltaY < 0;
    const isScrollingDown = event.deltaY > 0;
    if (this.drawerInner.scrollHeight > this.drawerInner.clientHeight) {
      event.preventDefault();
      isScrollingUp && (this.drawerInner.scrollTop -= 50);
      isScrollingDown && (this.drawerInner.scrollTop += 50);
    }
  }

  onScroll(event) {
    if (!this.isOpen) return;
    const isScrollingUp = event.deltaY < 0;
    const isScrollingDown = event.deltaY > 0;
    if (this.itemsCart.scrollHeight > this.itemsCart.clientHeight) {
      event.preventDefault();
      isScrollingUp && (this.itemsCart.scrollTop -= 50);
      isScrollingDown && (this.itemsCart.scrollTop += 50);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    window.scrollHtml.stop();
    this.isOpen = true;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    window.scrollHtml.start();
    this.isOpen = false;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
  }

  /**
   * The inherited handler re-renders from `main-cart-items`, which only exists on
   * the cart page — inside the drawer it has to come from the drawer section.
   */
  onCartUpdate() {
    return refreshCartDrawer();
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/** Fetches the `cart-drawer` section and returns it as a parsed document. */
async function fetchCartDrawerSection() {
  const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
  return new DOMParser().parseFromString(await response.text(), 'text/html');
}

/**
 * Re-renders the drawer — and the header cart bubble — from the server. Pass an
 * already-parsed section document to avoid fetching it twice.
 */
async function refreshCartDrawer(sectionDocument) {
  const parsed = sectionDocument || (await fetchCartDrawerSection());

  const source = parsed.querySelector('.drawer__inner');
  const target = document.querySelector('#CartDrawer .drawer__inner');
  if (source && target) target.innerHTML = source.innerHTML;

  const bubbleTarget = document.getElementById('cart-icon-bubble');
  if (bubbleTarget) {
    const bubbleResponse = await fetch(`${window.location.pathname}?section_id=cart-icon-bubble`);
    const bubbleDocument = new DOMParser().parseFromString(await bubbleResponse.text(), 'text/html');
    const bubbleSource = bubbleDocument.querySelector('.shopify-section');
    if (bubbleSource) bubbleTarget.innerHTML = bubbleSource.innerHTML;
  }

  return parsed;
}

/**
 * Collapsible discount code field. Applying and removing codes both go through
 * `/cart/update.js`, and success is confirmed by re-reading the codes Liquid
 * renders back — no reliance on undocumented Ajax response fields.
 */
class CartDiscount extends HTMLElement {
  connectedCallback() {
    if (this.isWired) return;
    this.isWired = true;

    this.toggle = this.querySelector('.cart-discount__toggle');
    this.panel = this.querySelector('.cart-discount__panel');
    this.input = this.querySelector('.cart-discount__input');
    this.submit = this.querySelector('.cart-discount__submit');
    this.errorMessage = this.querySelector('.cart-discount__error');

    if (this.toggle) this.toggle.addEventListener('click', this.togglePanel.bind(this));
    if (this.submit) this.submit.addEventListener('click', this.apply.bind(this));

    if (this.input) {
      this.input.addEventListener('input', this.clearError.bind(this));
      this.input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.apply();
      });
    }
  }

  get appliedCodes() {
    return (this.dataset.appliedCodes || '')
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);
  }

  togglePanel() {
    const isOpen = this.classList.toggle('is-open');
    if (this.panel) this.panel.hidden = !isOpen;
    if (this.toggle) this.toggle.setAttribute('aria-expanded', String(isOpen));
    if (isOpen && this.input && !this.input.readOnly) this.input.focus();
  }

  clearError() {
    this.classList.remove('cart-discount--error');
    if (this.errorMessage) this.errorMessage.hidden = true;
  }

  showError() {
    this.classList.add('cart-discount--error');
    if (this.errorMessage) this.errorMessage.hidden = false;
  }

  setBusy(isBusy) {
    this.classList.toggle('cart-discount--busy', isBusy);
    if (this.submit) this.submit.disabled = isBusy;
  }

  async apply() {
    if (this.classList.contains('cart-discount--applied') || !this.input) return;

    const code = this.input.value.trim();
    if (!code) return;

    this.clearError();
    this.setBusy(true);

    const codes = this.appliedCodes.concat(code);
    const { applied, sectionDocument } = await CartDiscount.updateDiscounts(codes);

    if (applied.some((value) => value.toLowerCase() === code.toLowerCase())) {
      await refreshCartDrawer(sectionDocument);
    } else {
      this.showError();
      this.setBusy(false);
    }
  }

  /**
   * Sends the full list of codes to the Ajax API, then reads back the codes the
   * storefront actually kept — Liquid is the source of truth here, so this works
   * without relying on undocumented Ajax response fields.
   */
  static async updateDiscounts(codes) {
    const body = JSON.stringify({ discount: codes.join(',') });

    try {
      await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
    } catch (error) {
      console.error(error);
      return { applied: [], sectionDocument: null };
    }

    const sectionDocument = await fetchCartDrawerSection();
    const discountElement = sectionDocument.querySelector('cart-discount');
    const applied = (discountElement?.dataset.appliedCodes || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    return { applied, sectionDocument };
  }
}

customElements.define('cart-discount', CartDiscount);

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-discount-remove]');
  if (!button) return;

  event.preventDefault();
  const removed = button.dataset.discountRemove;
  const discountElement = document.querySelector('cart-discount');
  const remaining = (discountElement ? discountElement.appliedCodes : []).filter(
    (code) => code.toLowerCase() !== removed.toLowerCase()
  );

  button.disabled = true;
  const { sectionDocument } = await CartDiscount.updateDiscounts(remaining);
  await refreshCartDrawer(sectionDocument);
});

/**
 * Keeps incentive gift products in sync with the cart: adds the gift once its
 * threshold is met, removes it as soon as the cart drops back below.
 *
 * Rules are serialised as `type:threshold:variantId:blockId|…`.
 */
class CartGiftManager extends HTMLElement {
  connectedCallback() {
    this.rules = (this.dataset.gifts || '')
      .split('|')
      .filter(Boolean)
      .map((rule) => {
        const [type, threshold, variantId, blockId] = rule.split(':');
        return { type, threshold: parseFloat(threshold), variantId, blockId };
      });

    this.unsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-gift-manager') return;
      this.sync();
    });

    this.sync();
  }

  disconnectedCallback() {
    if (this.unsubscriber) this.unsubscriber();
  }

  async sync() {
    if (!this.rules.length || this.isSyncing) return;
    this.isSyncing = true;

    try {
      const cart = await (await fetch(`${routes.cart_url}.js`)).json();
      const giftLines = cart.items.filter((item) => item.properties && item.properties._gift);
      const regularLines = cart.items.filter((item) => !(item.properties && item.properties._gift));

      const total = regularLines.reduce((sum, item) => sum + item.final_line_price, 0);
      const bottles = regularLines.reduce((sum, item) => sum + item.quantity, 0);

      let changed = false;

      for (const rule of this.rules) {
        const current = rule.type === 'quantity' ? bottles : total;
        const threshold = rule.type === 'quantity' ? rule.threshold : rule.threshold * 100;
        const reached = current >= threshold;
        const line = giftLines.find((item) => item.properties._gift === rule.blockId);

        if (reached && !line) {
          changed = (await this.addGift(rule)) || changed;
        } else if (!reached && line) {
          changed = (await this.removeGift(line)) || changed;
        }
      }

      if (changed) await refreshCartDrawer();
    } catch (error) {
      console.error(error);
    } finally {
      this.isSyncing = false;
    }
  }

  async addGift(rule) {
    const body = JSON.stringify({
      items: [{ id: rule.variantId, quantity: 1, properties: { _gift: rule.blockId } }],
    });

    const response = await fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } });
    return response.ok;
  }

  async removeGift(line) {
    const body = JSON.stringify({ id: line.key, quantity: 0 });
    const response = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } });
    return response.ok;
  }
}

customElements.define('cart-gift-manager', CartGiftManager);
